import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Permit, WelfareCheck } from '@app/models/permit.model';
import { PermitService } from '@app/services/permit/permit-service';
import { LocalizationService } from '../internationalization/localization.service';
import { Router } from '@angular/router';
import { UserService } from '@app/services/user/user.service';
import { PermitQnA, QuestionRule } from '@app/models/permitqna.model';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { SiteService } from '@app/services/site/site.service';
import { PermitStatus } from '@app/shared/enums';
import { UtilsService } from '@app/services/util/util-service';

@Component({
    selector: 'app-permit-welfare-checks',
    templateUrl: './permit-welfare-checks.component.html',
    styleUrls: ['./permit-welfare-checks.component.scss'],
    standalone: false
})
export class PermitWelfareChecksComponent implements OnInit {

  currentPermit: Permit | undefined;
  welfareCheck: WelfareCheck | undefined;
  questionArrays: any | [];
  locationOfWork: string = "";

  hasContentError: boolean = false;
  errorMessage: string = '';

  private hasErrorTrueRegex = /,"HasContentError":true/g;
  private hasErrorFalseRegex = /,"HasContentError":false/g;

  constructor(private _permitService: PermitService,
    private _localizationService: LocalizationService,
    private _router: Router,
    private _userService: UserService,
    private _navigationService: NavigationService,
    private _siteService: SiteService,
    private changeDetectorRef: ChangeDetectorRef,
    private _utilsService: UtilsService) {
    this._navigationService.onChange(false, false);
  }

  async ngOnInit(): Promise<void> {
    await this.getPermit();
    await this.getNewWelfareCheck();
    await this.getSiteName();
  }

  async getPermit() {
    this._userService.getSignIn().then(signInInfo => {
      if (signInInfo !== undefined && signInInfo.signInSignOutId !== "") {
        this._permitService.getPermitBySignInSignOutId(signInInfo.signInSignOutId).subscribe({
          next: (permit) => {
            if (permit) {
              this.currentPermit = permit;
              this.checkIfOpenPermitIsSubmitted();
            }
          }, error: (error) => {
            if (error.error && error.error.errorCode) {
              alert(this._localizationService.translate(error.error.errorCode));
            }
            else {
              alert(this._localizationService.translate("permit_welfare_checks_fetching_welfare_checks_error"));
            }
            console.error(this._localizationService.translate('permit_welfare_checks_fetching_welfare_checks_error', error));
          }
        });
      }
      else {
        alert(this._localizationService.translate('permit_welfare_checks_error_NoSignIn_Found'));
        this._router.navigate(['signin']);
      }
    });
  }

  async getNewWelfareCheck() {
    const site = await this._siteService.getSignedInSite();
    if(site){
      this._permitService.getNewWelfareCheck(site.brand.id, site.id).subscribe({
        next: (welfareCheck) => {
          this.questionArrays = JSON.parse(welfareCheck.qnA);
        },
        error: (error) => {
          console.error(this._localizationService.translate("permit_welfare_checks_fetching_welfare_checks_error", error));
        }
      });
    }else{
      alert(this._localizationService.translate('open_permit_no_site_found'));
    }
  }

  async getSiteName() {
    this._siteService.getSignedInSite().then(site => {
      if (site) {
        this.locationOfWork = site.name;
      }
    });
  }

  checkIfOpenPermitIsSubmitted() {
    if (this.currentPermit?.status !== PermitStatus.SUMBITTED) {
      alert(this._localizationService.translate("permit_welfare_checks_error_NoWelfareCheck_Found"));
      this._router.navigate(['siteinductioncompleted']);
    }
  }

  onSubmit() {
    this.hasContentError = this.performErrorValidation(this.questionArrays);

    if (!this.hasContentError) {
      let permitQnASanitized = this.sanitizePermitQnA(this.questionArrays);
      const base64QuestionArrays = this._utilsService.encodeBase64String(permitQnASanitized);

      this.welfareCheck = {
        id: undefined,
        qnA: base64QuestionArrays,
        permitId: this.currentPermit?.id
      }
      if (this.welfareCheck) {
        this.createWelfareCheckAnswer(this.welfareCheck);
      }

    } else {
      alert(this._localizationService.translate('permit_welfare_checks_required_fields_error'));
    }
  }

  performErrorValidation(QnAs: any): boolean {
    let errorsFound = false;

    if (QnAs) {
      QnAs.forEach((question: any) => {
        question.HasContentError = false;
        this.changeDetectorRef.detectChanges();

        if (question.IsRequired) {
          if (!question.Answer || question.Answer.trim() === "") {
            errorsFound = true;
            question.HasContentError = true;

            this.errorMessage = this._localizationService.translate('permit_welfare_checks_required_fields_error');
          }
          else {
            question.HasContentError = false;
          }

          this.changeDetectorRef.detectChanges();
        }

        // Validate nested Rules
        if (question.Rules !== null && question.Rules !== undefined && question.Rules.length > 0) {
          question.Rules.forEach((rule: any) => {
            if (String(question.Answer).includes(String(rule.Value))) {
              errorsFound = this.performErrorValidation(rule.RuleDetails);
            }
          });
        }

        // Validate nested Groups
        if (question.GroupingDetails !== null && question.GroupingDetails !== undefined && question.GroupingDetails.length > 0) {
          errorsFound = this.performErrorValidation(question.GroupingDetails);
        }
      });
    }

    return errorsFound;
  }

  private sanitizePermitQnA(permitQnAs: any): string {

    if (!permitQnAs) {
      return '';
    }

    let permitQnASanitized = JSON.stringify(permitQnAs)
      .replace(this.hasErrorTrueRegex, '')
      .replace(this.hasErrorFalseRegex, '');

    return permitQnASanitized;
  }



  createWelfareCheckAnswer(welfareCheck: WelfareCheck | undefined) {
    if (welfareCheck) {
      this._permitService.createWelfareCheckAnswer(welfareCheck).subscribe({
        next: (wc) => {
          if (wc) {
            alert(this._localizationService.translate('permit_welfare_checks_create_answer_welfare_checks_success'));
            this._router.navigate(['siteinductioncompleted']);
          }
        },
        error: (error) => {
          if (error.error && error.error.errorCode) {
            alert(this._localizationService.translate(error.error.errorCode));
          }
          else {
            alert(this._localizationService.translate("permit_welfare_checks_create_answer_welfare_checks_error"));
          }
          console.error(this._localizationService.translate("permit_welfare_checks_create_answer_welfare_checks_error", error));
        }
      })
    }
  }

  onCancel() {
    this._router.navigate(['siteinductioncompleted']);
  }

}
